/** @jsxImportSource @emotion/react */
import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Marker, useMapEvents } from 'react-leaflet';
import { useNavigate } from 'react-router-dom';
import L from 'leaflet';
import { createChargerIndicator } from './utils/createChargerIndicator';
import { ChargerMarkerProps } from './leaflet-map.types';
import { DEFAULT_ZOOM, ICON_MD_ZOOM, ICON_LG_ZOOM } from './leaflet-map.constants';
import { createLgChargerIndicator } from './utils/createLgChargerIndicator';
import { createMediumChargerIndicator } from './utils/createMediumChargerIndicator';
import { useLeafletMap } from './MapContext';

type IconSize = 'sm' | 'md' | 'lg';

export const ChargerMarker = (props: ChargerMarkerProps) => {
    const {
        unavailableConnectors = 0,
        availableConnectors = 0,
        totalConnectors = 0,
        busyConnectors = 0,
        price = '0.00 $',
        pricingOptionLabel = '',
        isFree = false,
        location,
        id,
    } = props;
    const navigate = useNavigate();
    const [iconSize, setIconSize] = useState<IconSize>('sm');
    const { flyToLocation } = useLeafletMap();

    const map = useMapEvents({
        zoom() {
            const currentZoom = map.getZoom();
            if (currentZoom <= DEFAULT_ZOOM) {
                setIconSize('sm');
            }

            if (currentZoom <= ICON_MD_ZOOM) {
                setIconSize('md');
            }

            if (currentZoom <= ICON_LG_ZOOM) {
                setIconSize('lg');
            }
        },
    });

    useEffect(() => {
        if (map) {
            const initialZoom = map.getZoom();

            if (initialZoom <= DEFAULT_ZOOM) {
                setIconSize('sm');
            }

            if (initialZoom <= ICON_MD_ZOOM) {
                setIconSize('md');
            }

            if (initialZoom <= ICON_LG_ZOOM) {
                setIconSize('lg');
            }
        }
    }, [map]);

    const navigateToLocation = useCallback((locationId: string) => {
        navigate(`/locations/${locationId}`);
    }, []);

    const handleMarkerClick = () => {
        if (iconSize === 'sm' && id) {
            navigateToLocation(id);
            return;
        }

        if (location?.[0] && location?.[1]) {
            flyToLocation(location);
        }
    };

    const markerIndicatorIcons = useMemo(() => {
        const smIndicator = createChargerIndicator({
            unavailableConnectors,
            availableConnectors,
            totalConnectors,
            busyConnectors,
            price,
            pricingOptionLabel,
            isFree,
        });

        const mdIndicator = createMediumChargerIndicator({
            unavailableConnectors,
            availableConnectors,
            totalConnectors,
            busyConnectors,
            price,
            pricingOptionLabel,
            isFree,
        });

        const lgIndicator = createLgChargerIndicator({
            unavailableConnectors,
            availableConnectors,
            totalConnectors,
            busyConnectors,
        });

        return {
            sm: smIndicator,
            md: mdIndicator,
            lg: lgIndicator,
        };
    }, [
        unavailableConnectors,
        availableConnectors,
        totalConnectors,
        busyConnectors,
        price,
        pricingOptionLabel,
        isFree,
    ]);

    const indicatorIcon = L.divIcon({
        className: '',
        html: markerIndicatorIcons[iconSize],
    });

    return (
        <Marker
            eventHandlers={{ click: handleMarkerClick }}
            position={location}
            icon={indicatorIcon}
        />
    );
};
